import usePaymentOrderOptions, { mergePaymentOrders } from "./usePaymentOrderOptions";
import usePaymentFormChoices from "./usePaymentFormChoices";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  Layout,
  Table,
  Button,
  Input,
  DatePicker,
  Descriptions,
  Select,
  Modal,
  Form,
  InputNumber,
  Checkbox,
  Space,
  message,
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Alert,
  Tag,
  Collapse,
  Empty,
  theme,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  LockOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useSelection } from "../SelectionContext";
import axios from "axios";
import dayjs from "dayjs";
import locale from "dayjs/locale/fr";
import "dayjs/locale/fr";
import * as XLSX from "xlsx";
import "./PaymentStyles.css"; // Importer les styles CSS
import { useAuth } from "../context/AuthContext"; // Use AuthContext
import type { TableProps } from "antd"; // Import TableProps
import API_CONFIG from '../config/api.js';
import RowActionsMenu from "../components/RowActionsMenu";
import ThemeContext from "../context/ThemeContext";
import "./payments-workspace.css";
import { useCommercialTreasuryAccountSuggestion } from "../hooks/useCommercialTreasurySuggestion";
import {
  buildPaymentCustomerOptions,
  type PaymentCustomerOption,
} from "./paymentEntityOptions";

// Configuration d'axios
axios.defaults.baseURL = API_CONFIG.BASE_URL;

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

dayjs.locale(locale);

type Customer = PaymentCustomerOption;


interface Order {
  id: number;
  invoice_number: string;
  order_date: string;
  total: number;
  paid_amount: number;
  due_amount: number;
  payment_status: string;
  selected?: boolean;
  payment_amount?: number;
  customer_name?: string;
}

interface Payment {
  id: number;
  payment_number: string;
  date: string;
  amount: number;
  payment_mode_name: string;
  entity_name: string;
  warehouse_name: string;
  company_name: string;
  online_order_id?: number | null;
  ecommerce_order_number?: string | null;
  ecommerce_payment_event?: "capture" | "refund" | null;
}

interface PaymentSummary {
  amount: number;
  payment_count: number;
  customer_count: number;
  by_mode: { payment_mode_id: number | null; name: string; amount: number; count: number }[];
}

const money = (value: unknown) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
  ? `${Number(value).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} FCFA`
  : "—";

const PaymentEntrant: React.FC = () => {
  const { selectedWarehouse, selectedCompany } = useSelection();
  const { token } = theme.useToken();
  const appearance = useContext(ThemeContext) as { isDarkMode?: boolean } | null;
  const workspaceStyle = {
    "--ip-bg": token.colorBgLayout,
    "--ip-surface": token.colorBgContainer,
    "--ip-text": token.colorText,
    "--ip-muted": token.colorTextSecondary,
    "--ip-heading": appearance?.isDarkMode ? token.colorTextHeading : token.colorPrimaryTextActive,
    "--ip-accent": appearance?.isDarkMode ? token.colorPrimaryText : token.colorPrimary,
    "--ip-tint": token.colorPrimaryBg,
    "--ip-fill": token.colorFillAlter,
    "--ip-line": token.colorBorderSecondary,
    "--ip-success": token.colorSuccessText,
    "--ip-warning": token.colorWarningText,
  } as React.CSSProperties;
  const [loading, setLoading] = useState<boolean>(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [remarksExpanded, setRemarksExpanded] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [unpaidOrders, setUnpaidOrders] = useState<Order[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [currentCustomerName, setCurrentCustomerName] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const paymentPage = pagination.current;
  const paymentPageSize = pagination.pageSize;
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [currentPayment, setCurrentPayment] = useState<any>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<number[]>([]);
  // État local pour gérer l'ID du magasin sélectionné
  const [currentWarehouseId, setCurrentWarehouseId] = useState<number | null>(
    null
  );
  // @ts-ignore - Context from JS file
  const { user, hasPermission } = useAuth(); // Get user object and hasPermission from AuthContext
  const canViewPayments = hasPermission("Gestion Commerciale.Ventes.PaiementsEntrants.view");
  const formEntityId = Form.useWatch("customer_id", form);
  const receivedAmount = Number(Form.useWatch("amount", form) || 0);
  const formChoices = usePaymentFormChoices({ userId: user?.id, companyId: selectedCompany,
    warehouseId: currentWarehouseId, paymentType: "in", active: modalVisible,
    enabled: hasPermission(`Gestion Commerciale.Ventes.PaiementsEntrants.${editMode ? "edit" : "create"}`) });
  const balances = usePaymentOrderOptions({ userId: user?.id, companyId: selectedCompany,
    warehouseId: currentWarehouseId, paymentType: "in", entityId: formEntityId,
    editingPaymentId: editMode ? currentPayment?.id : null, active: modalVisible,
    enabled: hasPermission(`Gestion Commerciale.Ventes.PaiementsEntrants.${editMode ? "edit" : "create"}`) });
  const treasuryAccounts = formChoices.treasuryAccounts;
  const accountsLoading = formChoices.loading;
  const paymentModes = formChoices.paymentModes;
  const loadingPaymentModes = formChoices.loading;
  const formEntities = buildPaymentCustomerOptions(formChoices.entities, currentWarehouseId);
  const savingPayment = useRef(false);
  const listRequest = useRef<{ id: number; controller?: AbortController }>({ id: 0 });
  const activeScope = useRef("");
  activeScope.current = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
  const exportFilterScope = useRef("");
  exportFilterScope.current = JSON.stringify([activeScope.current, searchText, selectedCustomer,
    dateRange?.[0]?.format("YYYY-MM-DD"), dateRange?.[1]?.format("YYYY-MM-DD")]);
  const previousContext = useRef(`${user?.id}:${selectedCompany}:${currentWarehouseId}`);
  useEffect(() => {
    const context = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
    if (previousContext.current !== context) {
      previousContext.current = context;
      setModalVisible(false);
      setUnpaidOrders([]);
      setSelectedCustomer(null);
      setCurrentCustomerName("");
      setSelectedPaymentIds([]);
      form.resetFields();
    }
  }, [user?.id, selectedCompany, currentWarehouseId, form]);
  useEffect(() => {
    // Form.useWatch can still be empty on the first opening render. Keep the
    // allocations loaded by openEditModal until its locked client is registered.
    if (modalVisible && !formEntityId && !editMode) setUnpaidOrders([]);
    else if (modalVisible && balances.data) {
      setUnpaidOrders((previous) => mergePaymentOrders(balances.data!.orders, previous));
    }
  }, [modalVisible, formEntityId, balances.data, editMode]);

  const { onModeChange: suggestTreasuryAccount } =
    useCommercialTreasuryAccountSuggestion(form, {
      warehouseId: currentWarehouseId,
      enabled: modalVisible,
      modeField: "payment_mode_id",
      accountField: "treasury_account_id",
      accounts: treasuryAccounts,
    });

  // Gérer le changement de magasin sélectionné
  useEffect(() => {
    // Extraire l'ID
    const selectedId = selectedWarehouse?.id || selectedWarehouse;
    
    // Utiliser une ref pour comparer avec la valeur précédente sans créer de boucle
    setCurrentWarehouseId((currentId) => {
      if (String(selectedId) !== String(currentId)) {
        // Réinitialiser la pagination à 1 pour montrer les nouveaux résultats depuis le début
        setPagination((prev) => ({ ...prev, current: 1 }));
        return selectedId ? Number(selectedId) : null;
      }
      return currentId;
    });
  }, [selectedWarehouse]); // Retirer currentWarehouseId des dépendances

  // Charger les paiements
  const fetchPayments = useCallback(async () => {
    listRequest.current.controller?.abort();
    const controller = new AbortController();
    const requestId = listRequest.current.id + 1;
    listRequest.current = { id: requestId, controller };
    const requestedScope = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
    setSummary(null);
    setPayments([]);
    setSelectedPaymentIds([]);
    setListError(null);
    // Ne pas faire d'appel API si aucun magasin n'est sélectionné
    if (!currentWarehouseId || !canViewPayments) {
      setListLoading(false);
      setPagination((prev) => ({ ...prev, total: 0 }));
      return;
    }

    setListLoading(true);
    try {
      const params: any = {
        payment_type: "in",
        page: paymentPage,
        limit: paymentPageSize,
        company_id: selectedCompany,
        include_summary: "1",
      };


      if (currentWarehouseId) {
        params.warehouse_id = currentWarehouseId;
      }

      if (searchText) {
        params.search = searchText;
      }

      if (selectedCustomer) {
        params.customer_id = selectedCustomer;
      }

      if (dateRange && dateRange[0] && dateRange[1]) {
        params.date_from = dateRange[0].format("YYYY-MM-DD");
        params.date_to = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get("/api/payments", { params, signal: controller.signal,
        headers: { "X-Company-Id": selectedCompany } });
      if (controller.signal.aborted || listRequest.current.id !== requestId || activeScope.current !== requestedScope) return;

      // Enrichir les données des paiements avec les noms des clients
      const paymentsData = response.data.payments || response.data || [];
      if (!Array.isArray(paymentsData)) throw new Error("Réponse des paiements invalide.");
      const totals = response.data.summary;
      setSummary(totals && Array.isArray(totals.by_mode)
        && [totals.amount, totals.payment_count, totals.customer_count].every((value) => typeof value === "number" && Number.isFinite(value))
        && totals.by_mode.every((mode: any) => typeof mode.amount === "number" && typeof mode.count === "number"
          && Number.isFinite(mode.amount) && Number.isFinite(mode.count)) ? totals : null);

      // Si nous avons des paiements et des clients
      if (paymentsData.length > 0 && customers.length > 0) {
        // Créer un dictionnaire des clients pour une recherche rapide
        const clientsMap = customers.reduce(
          (map: Record<number, Customer>, client: Customer) => {
            map[client.id] = client;
            return map;
          },
          {}
        );

        // Enrichir chaque paiement avec le nom du client si disponible
        const enrichedPayments = paymentsData.map((payment: any) => {
          // Si le paiement a un user_id et que ce user_id correspond à un client dans notre map
          if (payment.user_id && clientsMap[payment.user_id]) {
            return {
              ...payment,
              entity_name:
                clientsMap[payment.user_id].name || payment.entity_name,
            };
          }
          return payment;
        });

        setPayments(enrichedPayments);
      } else {
        setPayments(paymentsData);
      }

      setPagination((prev) => ({
        ...prev,
        total: Number(response.data.total ?? paymentsData.length),
      }));
    } catch (error) {
      if (controller.signal.aborted || listRequest.current.id !== requestId || activeScope.current !== requestedScope) return;
      setListError("Impossible de charger les paiements. Vos filtres sont conservés.");
    } finally {
      if (listRequest.current.id === requestId && !controller.signal.aborted) setListLoading(false);
    }
  }, [currentWarehouseId, selectedCompany, user?.id, canViewPayments, paymentPage, paymentPageSize, searchText, selectedCustomer, dateRange, customers]);

  // Ajouter cette fonction pour récupérer les détails d'un magasin
  const fetchWarehouseInfo = useCallback(async (warehouseId: number | null) => {
    if (!warehouseId) return null;
    try {
      const response = await axios.get(`/api/warehouses/${warehouseId}`);
      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  // Charger les clients
  const fetchCustomers = useCallback(async () => {
    const requestedScope = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
    // Ne pas charger les clients si aucun magasin n'est sélectionné
    if (!currentWarehouseId || !canViewPayments) {
      setCustomers([]);
      return;
    }

    try {
      const params: any = {};


      if (currentWarehouseId) {
        params.warehouseId = currentWarehouseId;

        // Récupérer les détails du magasin pour avoir l'ID de l'entreprise
        const warehouseInfo = await fetchWarehouseInfo(currentWarehouseId);
        if (warehouseInfo && warehouseInfo.company_id) {
          params.companyId = warehouseInfo.company_id;
        }
      }

      const response = await axios.get("/api/users/customers", { params,
        headers: { "X-Company-Id": selectedCompany } });
      if (activeScope.current !== requestedScope) return;
      setCustomers(
        buildPaymentCustomerOptions(response.data, currentWarehouseId)
      );
    } catch (error) {
      message.error("Erreur lors du chargement des clients");
    }
  }, [currentWarehouseId, selectedCompany, canViewPayments, user?.id, fetchWarehouseInfo]);

  // Charger les détails d'un paiement
  const fetchPaymentDetails = useCallback(
    async (paymentId: number) => {
      setLoading(true);
      try {
        // Récupérer les détails du paiement
        const response = await axios.get(`/api/payments/${paymentId}`);
        const paymentData = response.data;


        // Enrichir les données du paiement avec le nom du client
        if (paymentData.user_id && customers.length > 0) {
          // Rechercher le client dans notre liste de clients
          const client = customers.find((c) => c.id === paymentData.user_id);
          if (client) {
            paymentData.customer_name = client.name;
            paymentData.entity_name = client.name;
          } else {
            // Si le client n'est pas dans notre liste, essayer de le récupérer depuis l'API
            try {
              const clientResponse = await axios.get(
                `/api/users/${paymentData.user_id}`
              );
              paymentData.customer_name = clientResponse.data.name;
              paymentData.entity_name = clientResponse.data.name;
            } catch (clientError) {
            }
          }
        }

        // Si le paiement a des commandes associées et que nous n'avons pas encore trouvé le client
        if (
          !paymentData.customer_name &&
          paymentData.orders &&
          paymentData.orders.length > 0
        ) {
          const firstOrder = paymentData.orders[0];

          if (firstOrder.user_id) {
            // Rechercher d'abord dans notre liste de clients
            const client = customers.find((c) => c.id === firstOrder.user_id);
            if (client) {
              paymentData.customer_name = client.name;
              paymentData.entity_name = client.name;
            } else {
              // Sinon, récupérer depuis l'API
              try {
                const clientResponse = await axios.get(
                  `/api/users/${firstOrder.user_id}`
                );
                paymentData.customer_name = clientResponse.data.name;
                paymentData.entity_name = clientResponse.data.name;
              } catch (clientError) {
              }
            }
          }
        }

        setCurrentPayment(paymentData);
        setDetailModalVisible(true);
      } catch (error) {
        message.error("Erreur lors du chargement des détails du paiement");
      } finally {
        setLoading(false);
      }
    },
    [customers]
  );

  // Gérer le changement de pagination
  const handleTableChange = (pagination: any) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  // Gérer la recherche
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  // Gérer le changement de client
  const handleCustomerChange = (value: number) => {
    setSelectedCustomer(value);
    setPagination({ ...pagination, current: 1 });
  };

  // Gérer le changement de date
  const handleDateChange = (dates: any) => {
    setDateRange(dates);
    setPagination({ ...pagination, current: 1 });
  };

  // Effet pour recharger les données quand currentWarehouseId change
  useEffect(() => {

    if (currentWarehouseId) {
      // Recharger les clients et modes de paiement quand le magasin change
      fetchCustomers();
        // Les paiements seront rechargés par l'effet suivant
    } else {
      // Réinitialiser les données si aucun magasin n'est sélectionné
      setCustomers([]);
      setPayments([]);
    }
  }, [currentWarehouseId, fetchCustomers]);

  // Effet séparé pour recharger les paiements (inclut le premier chargement)
  useEffect(() => {
    void fetchPayments();
    return () => listRequest.current.controller?.abort();
  }, [fetchPayments]);

  // Ouvrir le modal pour ajouter un paiement
  const openAddModal = () => {
    if (!currentWarehouseId) {
      Modal.warning({
        title: "Établissement non sélectionné",
        content:
          "Veuillez sélectionner un établissement avant de créer ou modifier un paiement.",
      });
      return;
    }
    setEditMode(false);
    setRemarksExpanded(false);
    form.resetFields();

    // Recharger les modes de paiement pour le magasin actuel

    // Ajouter des valeurs par défaut pour éviter l'avertissement "form not connected"
    form.setFieldsValue({
      payment_date: dayjs(),
      amount: 0,
      customer_id: undefined,
      payment_mode_id: undefined,
      treasury_account_id: undefined,
      notes: "",
    });

    setUnpaidOrders([]);
    setCurrentCustomerName("");
    setModalVisible(true);
  };

  // Ouvrir le modal pour éditer un paiement
  const openEditModal = async (record: Payment) => {
    const openingScope = activeScope.current;
    if (record.online_order_id) {
      Modal.info({
        title: "Paiement e-commerce protégé",
        content:
          "Ce paiement est piloté depuis la commande en ligne. Utilisez l'écran Commandes en ligne pour enregistrer un remboursement ou corriger son rapprochement.",
      });
      return;
    }
    if (!currentWarehouseId) {
      Modal.warning({
        title: "Établissement non sélectionné",
        content:
          "Veuillez sélectionner un établissement avant de créer ou modifier un paiement.",
      });
      return;
    }
    setEditMode(true);
    setLoading(true);
    try {

      // Charger les modes de paiement pour le magasin actuel

      // Charger les détails du paiement
      const response = await axios.get(`/api/payments/${record.id}`);
      if (activeScope.current !== openingScope) return;
      const payment = response.data;
      setRemarksExpanded(Boolean(payment.notes));
      setCurrentPayment(payment);
      // Préremplir le formulaire
      form.setFieldsValue({
        customer_id: payment.entity_id || payment.user_id,
        payment_date: dayjs(payment.date),
        payment_mode_id: payment.payment_mode_id,
        treasury_account_id: payment.treasury_account_id || undefined,
        amount: payment.amount,
        notes: payment.notes,
      });


      setUnpaidOrders((payment.orders || []).map((op: any) => ({
        id: Number(op.order_id), invoice_number: op.invoice_number || "N/A",
        order_date: op.order_date || "", total: Number(op.order_total || 0),
        paid_amount: Number(op.order_paid_amount || 0),
        due_amount: Number(op.order_due_amount || 0),
        payment_status: op.order_payment_status || "", selected: true,
        payment_amount: Number(op.amount_applied ?? op.amount ?? 0),
      })));

      setModalVisible(true);
    } catch (error) {
      message.error("Erreur lors du chargement du paiement");
    } finally {
      setLoading(false);
    }
  };

  const handleFormCustomerChange = (value: number | undefined) => {
    form.setFieldsValue({ customer_id: value });
    setUnpaidOrders([]);
    setCurrentCustomerName(formEntities.find((item) => item.id === value)?.name || "");
  };

  // Gérer la sélection d'une commande
  const handleOrderSelection = (orderId: number, checked: boolean) => {
    const updatedOrders = [...unpaidOrders];
    const index = updatedOrders.findIndex((o) => o.id === orderId);
    if (index !== -1) {
      updatedOrders[index].selected = checked;
      if (checked && !updatedOrders[index].payment_amount) {
        updatedOrders[index].payment_amount = updatedOrders[index].due_amount;
      }
      setUnpaidOrders(updatedOrders);
    }
  };

  // Gérer le changement de montant pour une commande
  const handleOrderAmountChange = (orderId: number, amount: number) => {
    const updatedOrders = [...unpaidOrders];
    const index = updatedOrders.findIndex((o) => o.id === orderId);
    if (index !== -1) {
      updatedOrders[index].payment_amount = amount;
      setUnpaidOrders(updatedOrders);
    }
  };

  // Calculer le montant total des commandes sélectionnées
  const calculateTotalSelectedAmount = () => {
    return unpaidOrders
      .filter((o) => o.selected)
      .reduce((sum, order) => sum + (order.payment_amount || 0), 0);
  };

  // NOUVEAU: Calculer le montant total DÛ des commandes sélectionnées
  // Soumettre le formulaire
  const handleSubmit = async (values: any) => {

    // Récupérer les commandes sélectionnées
    const selectedOrders = unpaidOrders
      .filter((o) => o.selected)
      .map((o) => ({
        order_id: o.id,
        amount: o.payment_amount || 0,
      }));


    // Vérifier si des commandes sont sélectionnées
    if (selectedOrders.length === 0 && !editMode) {
      Modal.confirm({
        title: "Aucune commande sélectionnée",
        content:
          "Vous n'avez sélectionné aucune commande à régler. Voulez-vous quand même enregistrer ce paiement sans l'affecter à des commandes?",
        okText: "Oui, continuer",
        cancelText: "Non, revenir",
        onOk: () => processPayment(values, selectedOrders),
      });
      return;
    }

    // Calculer le total des montants sélectionnés
    const totalSelectedAmount = calculateTotalSelectedAmount();

    // Vérifier la cohérence entre le montant saisi et le total des commandes
    if (Math.abs(totalSelectedAmount - values.amount) > 1 && !editMode) {
      // Tolérance de 1 pour les erreurs d'arrondi
      Modal.confirm({
        title: "Montants différents",
        content: `Le montant total que vous avez saisi (${values.amount.toLocaleString(
          "fr-FR"
        )} XOF) est différent du total des montants affectés aux commandes (${totalSelectedAmount.toLocaleString(
          "fr-FR"
        )} XOF). Voulez-vous continuer quand même?`,
        okText: "Oui, continuer",
        cancelText: "Non, revenir",
        onOk: () => processPayment(values, selectedOrders),
      });
    } else {
      // Si tout est cohérent, procéder directement
      processPayment(values, selectedOrders);
    }
  };

  // Fonction pour traiter le paiement après validation
  const processPayment = async (values: any, selectedOrders: any[]) => {
    if (savingPayment.current) return;
    savingPayment.current = true;
    setLoading(true);
    try {
      // Vérifier que le magasin est sélectionné
      if (!currentWarehouseId) {
        message.error(
          "Veuillez sélectionner un établissement avant de créer ou modifier un paiement"
        );
        setLoading(false);
        return;
      }

      const warehouseId = Number(currentWarehouseId);
      const warehouseInfo = await fetchWarehouseInfo(warehouseId);

      if (!warehouseInfo) {
        message.error("Impossible de récupérer les informations de l'établissement");
        setLoading(false);
        return;
      }

      await balances.verify(selectedOrders);

      // Préparer les données du paiement
      const paymentData = {
        company_id: selectedCompany,
        warehouse_id: warehouseId,
        payment_type: "in",
        date: values.payment_date.format("YYYY-MM-DD"),
        amount: values.amount,
        payment_mode_id: values.payment_mode_id,
        treasury_account_id: values.treasury_account_id,
        user_id: values.customer_id,
        notes: values.notes,
        orders: selectedOrders,
      };


      if (editMode && currentPayment) {
        await axios.put(`/api/payments/${currentPayment.id}`, paymentData);
        message.success("Paiement mis à jour avec succès");
      } else {
        await axios.post("/api/payments", paymentData);
        message.success("Paiement créé avec succès");
      }

      void balances.invalidate();
      setModalVisible(false);
      fetchPayments();
    } catch (error: any) {
      if (error.response?.data?.code === "ORDER_PAYMENT_BALANCE_CHANGED") {
        void balances.refresh().catch(() => {});
      }
      message.error(error.response?.data?.error || error.message || "Erreur lors de l'enregistrement du paiement");
    } finally {
      savingPayment.current = false;
      setLoading(false);
    }
  };

  // Supprimer un paiement
  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer ce paiement ?",
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        setLoading(true);
        try {
          await axios.delete(`/api/payments/${id}`);
          void balances.invalidate();
          message.success("Paiement supprimé avec succès");
          fetchPayments();
        } catch (error) {
          message.error("Erreur lors de la suppression du paiement");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // New functions for multi-selection and export
  const handlePaymentSelection = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedPaymentIds((prev) => [...prev, id]);
    } else {
      setSelectedPaymentIds((prev) =>
        prev.filter((paymentId) => paymentId !== id)
      );
    }
  };

  const handleSelectAllPayments = (checked: boolean) => {
    if (checked) {
      const allIds = payments
        .filter((payment) => !payment.online_order_id)
        .map((payment) => payment.id);
      setSelectedPaymentIds(allIds);
    } else {
      setSelectedPaymentIds([]);
    }
  };

  const handleMultipleDelete = () => {
    if (selectedPaymentIds.length === 0) return;
    Modal.confirm({
      title: `Êtes-vous sûr de vouloir supprimer ${selectedPaymentIds.length} paiement(s) ?`,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        setLoading(true);
        try {
          const deletePromises = selectedPaymentIds.map((id) =>
            axios.delete(`/api/payments/${id}`).then((response) => {
              void balances.invalidate();
              return response;
            })
          );
          await Promise.all(deletePromises);
          message.success(
            `${selectedPaymentIds.length} paiement(s) supprimé(s) avec succès`
          );
          fetchPayments();
          setSelectedPaymentIds([]);
        } catch (error) {
          message.error("Erreur lors de la suppression des paiements");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleExportToExcel = async () => {
    if (!currentWarehouseId || !canViewPayments) return;
    const exportScope = activeScope.current;
    const exportedFilters = exportFilterScope.current;
    setExportLoading(true);
    message.loading({ content: "Préparation de l’export Excel…", key: "exportLoading" });
    try {
      const params: any = { payment_type: "in", company_id: selectedCompany,
        warehouse_id: currentWarehouseId, limit: 200 };
      if (searchText) params.search = searchText;
      if (selectedCustomer) params.customer_id = selectedCustomer;
      if (dateRange?.[0] && dateRange?.[1]) {
        params.date_from = dateRange[0].format("YYYY-MM-DD");
        params.date_to = dateRange[1].format("YYYY-MM-DD");
      }
      const rows = new Map<number, Payment>();
      let expectedTotal: number | null = null;
      for (let page = 1; ; page += 1) {
        const response = await axios.get("/api/payments", { params: { ...params, page },
          headers: { "X-Company-Id": selectedCompany } });
        if (activeScope.current !== exportScope || exportFilterScope.current !== exportedFilters) {
          throw new Error("Le contexte ou les filtres ont changé. Relancez l’export.");
        }
        const batch = response.data.payments;
        const total = Number(response.data.total);
        if (!Array.isArray(batch) || !Number.isInteger(total) || total < 0) {
          throw new Error("Réponse d’export invalide. Aucun fichier n’a été créé.");
        }
        if (expectedTotal === null) expectedTotal = total;
        if (expectedTotal !== total || (batch.length === 0 && rows.size < total)) {
          throw new Error("La liste a changé pendant l’export. Relancez l’export pour obtenir un fichier complet.");
        }
        for (const payment of batch) {
          if (rows.has(payment.id)) throw new Error("La liste a changé pendant l’export. Relancez l’export.");
          rows.set(payment.id, payment);
        }
        if (rows.size === expectedTotal) break;
        if (rows.size > expectedTotal) throw new Error("La liste a changé pendant l’export. Relancez l’export.");
      }
      const formattedData = Array.from(rows.values()).map((payment) => ({
        Date: dayjs(payment.date).format("DD/MM/YYYY"), "No Transaction": payment.payment_number,
        Client: payment.entity_name, Établissement: payment.warehouse_name,
        "Mode de paiement": payment.payment_mode_name, Montant: payment.amount,
        Devise: "XOF", Entreprise: payment.company_name,
      }));
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      worksheet["!cols"] = [12, 22, 30, 30, 20, 18, 10, 24].map((wch) => ({ wch }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Paiements Entrants");
      XLSX.writeFile(workbook, "Paiements_Entrants_" + dayjs().format("YYYY-MM-DD") + ".xlsx");
      message.success({ content: "Export Excel complet : " + rows.size + " paiement(s).", key: "exportLoading" });
    } catch (error: any) {
      message.error({ content: error.message || "Erreur lors de l’export Excel", key: "exportLoading" });
    } finally {
      setExportLoading(false);
    }
  };

  const establishmentName = (typeof selectedWarehouse === "object" && selectedWarehouse?.name)
    || payments[0]?.warehouse_name || "Établissement sélectionné";
  const companyName = payments[0]?.company_name;
  const allocatedAmount = calculateTotalSelectedAmount();
  const allocationGap = receivedAmount - allocatedAmount;
  const selectedOrderCount = unpaidOrders.filter((order) => order.selected).length;
  const periodLabel = dateRange?.[0] && dateRange?.[1]
    ? "Du " + dateRange[0].format("DD/MM/YYYY") + " au " + dateRange[1].format("DD/MM/YYYY")
    : "Toutes les périodes";
  const amountInputProps = {
    formatter: (value: any) => value ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "",
    parser: (value: any) => value ? Number(value.replace(/\s/g, "")) : 0,
  };
  const orderColumns: TableProps<Order>["columns"] = [
    { title: <Checkbox aria-label="Sélectionner toutes les commandes"
        checked={unpaidOrders.length > 0 && unpaidOrders.every((order) => order.selected)}
        indeterminate={selectedOrderCount > 0 && selectedOrderCount < unpaidOrders.length}
        onChange={(event) => setUnpaidOrders((previous) => previous.map((order) => ({ ...order,
          selected: event.target.checked,
          payment_amount: event.target.checked ? (order.payment_amount || order.due_amount) : order.payment_amount,
        })))} />, key: "selection", width: 44,
      render: (_, order) => <Checkbox checked={order.selected}
        aria-label={"Sélectionner la commande " + order.invoice_number}
        onChange={(event) => handleOrderSelection(order.id, event.target.checked)} /> },
    { title: "N° de facture", dataIndex: "invoice_number", key: "invoice", width: 150,
      render: (value, order) => <div className="payment-workspace__reference">{value}
        <span className="payment-workspace__small-date">{dayjs(order.order_date).format("DD/MM/YYYY")}</span></div> },
    { title: "Date", dataIndex: "order_date", key: "date", width: 110, responsive: ["lg"],
      render: (value) => dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY") : "—" },
    { title: "Statut", dataIndex: "payment_status", key: "status", width: 175, responsive: ["md"],
      render: (value) => <Tag color={String(value).toLowerCase().includes("non") ? "red" : "orange"}
        icon={<ExclamationCircleOutlined />}>{value || "Non défini"}</Tag> },
    { title: "Total", dataIndex: "total", key: "total", align: "right", width: 140,
      responsive: ["lg"], render: money },
    { title: "Montant dû", dataIndex: "due_amount", key: "due", align: "right", width: 140,
      render: money },
    { title: "À affecter (FCFA)", key: "allocation", width: 180, align: "right",
      render: (_, order) => <InputNumber {...amountInputProps} min={0}
        disabled={!order.selected} value={order.payment_amount}
        aria-label={"Montant affecté à " + order.invoice_number}
        onChange={(value) => handleOrderAmountChange(order.id, Number(value || 0))} /> },
  ];
  const selectablePayments = payments.filter((payment) => !payment.online_order_id);
  const columns: TableProps<Payment>["columns"] = [
    { title: <Checkbox aria-label="Sélectionner tous les paiements de la page"
        onChange={(event) => handleSelectAllPayments(event.target.checked)}
        checked={selectablePayments.length > 0 && selectedPaymentIds.length === selectablePayments.length}
        indeterminate={selectedPaymentIds.length > 0 && selectedPaymentIds.length < selectablePayments.length} />,
      key: "selection", width: 44,
      render: (_, record) => <Checkbox checked={selectedPaymentIds.includes(record.id)}
        aria-label={"Sélectionner le paiement " + record.payment_number}
        disabled={Boolean(record.online_order_id)}
        onChange={(event) => handlePaymentSelection(record.id, event.target.checked)} /> },
    { title: "Date", dataIndex: "date", key: "date", width: 112, responsive: ["lg"],
      render: (value) => dayjs(value).format("DD/MM/YYYY") },
    { title: "N° de transaction", dataIndex: "payment_number", key: "reference", width: 190,
      render: (value, record) => <div className="payment-workspace__reference">{value}
        <span className="payment-workspace__small-date">{dayjs(record.date).format("DD/MM/YYYY")}</span>
        {record.online_order_id && <Tag color="blue">E-commerce · {record.ecommerce_order_number}</Tag>}</div> },
    { title: "Client", dataIndex: "entity_name", key: "client", ellipsis: true },
    { title: "Établissement", dataIndex: "warehouse_name", key: "warehouse", responsive: ["lg"],
      ellipsis: true, className: "payment-workspace__muted" },
    { title: "Mode de paiement", dataIndex: "payment_mode_name", key: "mode", width: 150 },
    { title: "Montant", dataIndex: "amount", key: "amount", width: 165, align: "right",
      className: "payment-workspace__amount", render: money },
    { title: "Actions", key: "actions", width: 100, align: "center",
      render: (_, record) => {
        const protectedPayment = Boolean(record.online_order_id);
        const actionItems: any[] = [];
        if (hasPermission("Gestion Commerciale.Ventes.PaiementsEntrants.edit") && !protectedPayment) {
          actionItems.push({ key: "edit", icon: <EditOutlined aria-hidden="true" />, label: "Modifier",
            onClick: () => openEditModal(record) });
        }
        if (hasPermission("Gestion Commerciale.Ventes.PaiementsEntrants.delete") && !protectedPayment) {
          if (actionItems.length) actionItems.push({ type: "divider" });
          actionItems.push({ key: "delete", icon: <DeleteOutlined aria-hidden="true" />, label: "Supprimer", danger: true,
            onClick: () => handleDelete(record.id) });
        }
        if (protectedPayment) actionItems.push({ key: "ecommerce-info", icon: <LockOutlined />,
          label: "Piloté par " + (record.ecommerce_order_number || "la commande e-commerce"), disabled: true });
        return <Space size={0}>
          {canViewPayments && <Button type="text" icon={<EyeOutlined />}
            aria-label={"Voir le détail du paiement " + record.payment_number}
            onClick={() => fetchPaymentDetails(record.id)} />}
          {actionItems.length > 0 && <RowActionsMenu items={actionItems}
            ariaLabel={"Afficher les actions du paiement " + record.payment_number} />}
        </Space>;
      } },
  ];

  // Vérifier si l'utilisateur a la permission de voir le module
  if (!hasPermission("Gestion Commerciale.Ventes.PaiementsEntrants.view")) {
    return (
      <Layout className="site-layout">
        <Content style={{ margin: "0 16px" }}>
          <div style={{ padding: 24, minHeight: 360 }}>
            <Title level={2}>Paiements Entrants</Title>
            <p>
              Vous n'avez pas les permissions nécessaires pour voir cette
              section.
            </p>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="payment-workspace" style={workspaceStyle}>
      <Content>
        <div className="payment-workspace__workspace">
          <header className="payment-workspace__header">
            <div><Title level={2}>Paiements entrants</Title>
              <p>Une lecture claire des règlements reçus</p></div>
            <Space wrap className="payment-workspace__actions">
              <Button icon={<FileExcelOutlined aria-hidden="true" />} aria-label="Exporter sur Excel" onClick={handleExportToExcel}
                loading={exportLoading} disabled={!currentWarehouseId || listLoading || Boolean(listError) || !pagination.total}>
                Exporter sur Excel</Button>
              {hasPermission("Gestion Commerciale.Ventes.PaiementsEntrants.create") &&
                <Button type="primary" icon={<PlusOutlined aria-hidden="true" />} aria-label="Nouveau paiement" onClick={openAddModal}
                  disabled={!currentWarehouseId}>Nouveau paiement</Button>}
            </Space>
          </header>

          {!currentWarehouseId ? <Alert type="info" showIcon
            message="Sélection d’établissement requise"
            description="Sélectionnez un établissement dans le menu en haut à droite pour consulter et gérer ses paiements entrants." /> : <>
            <section className="payment-workspace__overview" aria-label="Synthèse des encaissements" aria-busy={listLoading}>
              <div className="payment-workspace__period"><CalendarOutlined /> {periodLabel}</div>
              <div className="payment-workspace__metrics">
                <div className="payment-workspace__metric payment-workspace__metric--total">
                  <strong>{listLoading ? "Chargement…" : summary ? money(summary.amount) : "—"}</strong>
                  <span>Total des paiements filtrés</span>
                </div>
                {!listLoading && summary?.by_mode.map((mode) => <div className="payment-workspace__metric"
                  key={mode.payment_mode_id ?? "unknown"}>
                  <strong>{money(mode.amount)}</strong>
                  <span>{mode.name} · {mode.count.toLocaleString("fr-FR")} paiement{mode.count > 1 ? "s" : ""}</span>
                </div>)}
              </div>
              {!listLoading && !summary && !listError && <Text type="secondary">Synthèse indisponible pour cette liste.</Text>}
            </section>

            <section className="payment-workspace__filters" aria-label="Filtres des paiements entrants">
              <div><label htmlFor="incoming-transaction-search">N° de transaction</label>
                <Input id="incoming-transaction-search" prefix={<SearchOutlined />} allowClear value={searchText}
                  placeholder="Rechercher une transaction" onChange={(event) => handleSearch(event.target.value)} /></div>
              <div><label htmlFor="incoming-customer-filter">Client</label>
                <Select id="incoming-customer-filter" placeholder="Tous les clients" allowClear showSearch
                  optionFilterProp="children" value={selectedCustomer} onChange={handleCustomerChange}>
                  {customers.map((customer) => <Option key={customer.id} value={customer.id}>{customer.name}</Option>)}
                </Select></div>
              <div><label htmlFor="incoming-period-start">Période</label>
                <RangePicker id={{ start: "incoming-period-start", end: "incoming-period-end" }}
                  format="DD/MM/YYYY" placeholder={["Date de début", "Date de fin"]}
                  value={dateRange} onChange={handleDateChange} /></div>
            </section>

            {listError && <Alert type="error" showIcon message="Paiements indisponibles" description={listError}
              action={<Button onClick={() => void fetchPayments()}>Réessayer</Button>} />}
            <section className="payment-workspace__journal" aria-labelledby="incoming-payment-journal-title">
              <div className="payment-workspace__journal-heading">
                <h3 id="incoming-payment-journal-title">Journal des paiements</h3>
                <span>{listLoading ? "Chargement…" : listError ? "Résultats indisponibles"
                  : pagination.total.toLocaleString("fr-FR") + " paiement" + (pagination.total > 1 ? "s" : "")
                    + (summary ? " · " + summary.customer_count.toLocaleString("fr-FR") + " client" + (summary.customer_count > 1 ? "s" : "") + " identifié" + (summary.customer_count > 1 ? "s" : "") : "")}</span>
              </div>
              {selectedPaymentIds.length > 0 && hasPermission("Gestion Commerciale.Ventes.PaiementsEntrants.delete") &&
                <div className="payment-workspace__selection"><span>{selectedPaymentIds.length} paiement(s) sélectionné(s)</span>
                  <Button danger icon={<DeleteOutlined />} onClick={handleMultipleDelete}>Supprimer la sélection</Button></div>}
              <Table columns={columns} dataSource={payments} rowKey="id" loading={listLoading}
                tableLayout="fixed" scroll={{ x: 760 }} onChange={handleTableChange}
                locale={{ emptyText: listError ? "La liste n’a pas pu être chargée." : "Aucun paiement pour ces filtres." }}
                pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
                  showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40", "50"],
                  showTotal: (total, range) => total ? "Affichage de " + range[0] + " à " + range[1] + " sur " + total + " paiements" : "0 paiement" }} />
            </section>
          </>}

          <Modal className="payment-entry-modal" open={modalVisible} centered
            title={<div className="payment-entry-modal__heading"><div>
              <Title level={3}>{editMode ? "Modifier le paiement" : "Nouveau paiement"}</Title>
              <p>Renseignez le règlement, puis choisissez les commandes à régler.</p></div>
              <span className="payment-entry-modal__context"><BankOutlined /> {companyName ? companyName + " · " : ""}{establishmentName}</span></div>}
            onCancel={() => setModalVisible(false)} footer={null} width="calc(100vw - 64px)"
            style={{ ...workspaceStyle, maxWidth: 1440 }}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}
              initialValues={{ payment_date: dayjs(), amount: 0 }}>
              <div className="payment-entry-modal__body">
                <BusinessResourceNotice resource={formChoices} label="listes de choix" />
                <BusinessResourceNotice resource={balances} label="commandes et soldes" />
                {unpaidOrders.some((row) => row.selected && Number(row.payment_amount) > Number(row.due_amount) + 0.0001) &&
                  <Alert type="warning" showIcon message="Le solde de certaines commandes a changé."
                    description="Les affectations sont conservées. Vérifiez les montants avant d'enregistrer." />}
                <div className="payment-entry-modal__fields">
                  <Form.Item name="customer_id" label="Client" className="payment-entry-modal__client"
                    rules={[{ required: true, message: "Veuillez sélectionner un client" }]}>
                    <Select placeholder="Sélectionner un client" showSearch optionFilterProp="children"
                      onChange={handleFormCustomerChange} disabled={editMode}>
                      {formEntities.map((customer) => <Option key={customer.id} value={customer.id}>{customer.name}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="payment_date" label="Date de paiement" className="payment-entry-modal__date"
                    rules={[{ required: true, message: "Veuillez sélectionner une date" }]}>
                    <DatePicker format="DD/MM/YYYY" />
                  </Form.Item>
                  <Form.Item name="amount" label="Montant reçu" className="payment-entry-modal__amount"
                    rules={[{ required: true, message: "Veuillez saisir un montant" }]}>
                    <InputNumber {...amountInputProps} min={0} addonAfter="FCFA" />
                  </Form.Item>
                  <Form.Item name="payment_mode_id" label="Mode de paiement" className="payment-entry-modal__mode"
                    rules={[{ required: true, message: "Veuillez sélectionner un mode de paiement" }]}>
                    <Select onChange={suggestTreasuryAccount} placeholder="Sélectionner un mode de paiement"
                      loading={loadingPaymentModes} disabled={!currentWarehouseId || loadingPaymentModes}
                      showSearch optionFilterProp="children"
                      notFoundContent={loadingPaymentModes ? <Spin size="small" /> : "Aucun mode de paiement trouvé"}>
                      {paymentModes.map((mode) => <Option key={mode.id} value={mode.id}>{mode.name}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="treasury_account_id" label="Compte de trésorerie" className="payment-entry-modal__account"
                    rules={[{ required: true, message: "Veuillez sélectionner un compte de trésorerie" }]}
                    extra={!formEntityId ? "Sélectionnez un client pour vérifier ses commandes avant l’enregistrement." : undefined}>
                    <Select placeholder="Sélectionner un compte de trésorerie" loading={accountsLoading}
                      disabled={accountsLoading || treasuryAccounts.length === 0 || balances.loading || !balances.data}>
                      {treasuryAccounts.map((account: any) => <Option key={account.id} value={account.id}>{account.name}</Option>)}
                    </Select>
                  </Form.Item>
                </div>
                {formChoices.status === "verified" && treasuryAccounts.length === 0 && <Alert type="warning" showIcon
                  message="Aucun compte de trésorerie disponible"
                  description="Configurez un compte commercial actif pour cet établissement avant d'enregistrer un paiement." />}

                <section className="payment-entry-modal__orders" aria-label="Commandes à payer">
                  <div className="payment-entry-modal__orders-heading">
                    <h4>Commandes à payer{currentCustomerName ? " · " + currentCustomerName : ""}</h4>
                    <Space wrap><Text type="secondary">{selectedOrderCount} sélectionnée{selectedOrderCount > 1 ? "s" : ""}</Text>
                      {formEntityId && <Button type="link" icon={<ReloadOutlined />} loading={balances.loading}
                        aria-label="Actualiser les commandes" onClick={() => void balances.refresh().catch(() => {})}>Actualiser</Button>}
                    </Space>
                  </div>
                  {unpaidOrders.length > 0 ? <Table columns={orderColumns} dataSource={unpaidOrders}
                    rowKey="id" pagination={false} size="small" tableLayout="fixed" scroll={{ y: 280 }}
                    rowClassName={(order) => order.selected ? "payment-entry-modal__order--selected" : ""} />
                    : formEntityId && balances.status === "verified" ? <Alert type="info" showIcon
                      message="Aucune commande à payer"
                      description="Ce client n'a aucune commande non payée ou partiellement payée en attente de règlement. Vous pouvez enregistrer un paiement sans affectation après confirmation." />
                    : <div className="payment-entry-modal__empty" aria-busy={balances.loading}>
                      {balances.loading ? <Spin tip="Lecture des commandes…" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={formEntityId ? "Les commandes sont indisponibles. Réessayez leur actualisation." : "Sélectionnez un client pour afficher ses commandes à payer."} />}
                    </div>}
                </section>

                <Collapse className="payment-entry-modal__remarks" activeKey={remarksExpanded ? ["notes"] : []}
                  onChange={(keys) => setRemarksExpanded(keys.includes("notes"))}
                  items={[{ key: "notes", label: "Ajouter une remarque (facultatif)", forceRender: true,
                    children: <Form.Item name="notes" label="Remarques"><Input.TextArea rows={3} /></Form.Item> }]} />

                <section className="payment-entry-modal__totals" aria-label="Récapitulatif du paiement" aria-live="polite">
                  <div><span>Reçu</span><strong>{money(receivedAmount)}</strong></div>
                  <div><span>Affecté</span><strong>{money(allocatedAmount)}</strong></div>
                  <div className={receivedAmount > 0 && Math.abs(allocationGap) <= 1
                    ? "payment-entry-modal__gap--matched" : "payment-entry-modal__gap--check"}>
                    {receivedAmount > 0 && Math.abs(allocationGap) <= 1 ? <CheckCircleFilled /> : <InfoCircleOutlined />}
                    <div><span>Écart</span><strong>{money(allocationGap)}</strong></div>
                  </div>
                  <p><InfoCircleOutlined /> Les soldes seront revérifiés à l’enregistrement.</p>
                </section>
              </div>
              <footer className="payment-entry-modal__footer">
                <Text type="secondary">{selectedOrderCount} commande{selectedOrderCount > 1 ? "s" : ""} sélectionnée{selectedOrderCount > 1 ? "s" : ""}</Text>
                <Space wrap><Button onClick={() => setModalVisible(false)}>Annuler</Button>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined aria-hidden="true" />} loading={loading}
                    aria-label={editMode ? "Mettre à jour le paiement" : "Enregistrer le paiement"}
                    disabled={accountsLoading || treasuryAccounts.length === 0 || balances.loading || !balances.data}>
                    {editMode ? "Mettre à jour le paiement" : "Enregistrer le paiement"}</Button>
                </Space>
              </footer>
            </Form>
          </Modal>

          {/* Modal de détail du paiement */}
          <Modal
            title="Détail du paiement"
            open={detailModalVisible}
            onCancel={() => setDetailModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setDetailModalVisible(false)}>
                Fermer
              </Button>,
            ]}
            width="95%"
            style={{ maxWidth: 700, top: 20 }}
          >
            {currentPayment ? (
              <div>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small" title="Informations générales">
                      <p>
                        <FileTextOutlined /> <strong>No Transaction:</strong>{" "}
                        {currentPayment.payment_number}
                      </p>
                      <p>
                        <CalendarOutlined /> <strong>Date:</strong>{" "}
                        {dayjs(currentPayment.date).format("DD/MM/YYYY")}
                      </p>
                      <p>
                        <UserOutlined /> <strong>Client:</strong>{" "}
                        {currentPayment.customer_name ||
                          currentPayment.entity_name ||
                          "Client inconnu"}
                      </p>
                      <p>
                        <BankOutlined /> <strong>Mode de paiement:</strong>{" "}
                        {currentPayment.payment_mode_name}
                      </p>
                      <p>
                        <strong>Montant:</strong>{" "}
                        {typeof currentPayment.amount === "number"
                          ? currentPayment.amount.toLocaleString("fr-FR", {
                              style: "currency",
                              currency: "XOF",
                            })
                          : "N/A"}
                      </p>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Informations complémentaires">
                      <p>
                        <strong>Établissement:</strong>{" "}
                        {currentPayment.warehouse_name}
                      </p>
                      <p>
                        <strong>Entreprise:</strong>{" "}
                        {currentPayment.company_name}
                      </p>
                      <p>
                        <strong>Remarques:</strong>{" "}
                        {currentPayment.notes || "Aucune"}
                      </p>
                      {currentPayment.online_order_id && (
                        <Alert
                          type="info"
                          showIcon
                          icon={<LockOutlined />}
                          message={`Paiement piloté par la commande e-commerce ${currentPayment.ecommerce_order_number}`}
                          description="Son remboursement doit être enregistré depuis Commandes en ligne afin de conserver la piste d'audit."
                        />
                      )}
                    </Card>
                  </Col>
                </Row>

                {currentPayment.orders && currentPayment.orders.length > 0 && (
                  <Card
                    size="small"
                    title="Commandes associées"
                    style={{ marginTop: 16 }}
                  >
                    <div className="overflow-x-auto">
                      <Table
                        dataSource={currentPayment.orders}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        columns={[
                          {
                            title: "No Facture",
                            dataIndex: "invoice_number",
                            key: "invoice_number",
                          },
                          {
                            title: "Montant total",
                            dataIndex: "order_total",
                            key: "total",
                            render: (text: number) =>
                              typeof text === "number"
                                ? text.toLocaleString("fr-FR", {
                                    style: "currency",
                                    currency: "XOF",
                                  })
                                : "N/A",
                          },
                          {
                            title: "Montant payé",
                            dataIndex: "amount_applied",
                            key: "amount",
                            render: (text: number) =>
                              typeof text === "number"
                                ? text.toLocaleString("fr-FR", {
                                    style: "currency",
                                    currency: "XOF",
                                  })
                                : "N/A",
                          },
                        ]}
                      />
                    </div>
                  </Card>
                )}

                {currentPayment.ecommerce_orders &&
                  currentPayment.ecommerce_orders.length > 0 && (
                    <Card
                      size="small"
                      title="Commande e-commerce associée"
                      style={{ marginTop: 16 }}
                    >
                      {currentPayment.ecommerce_orders.map((link: any) => (
                        <Descriptions
                          key={`${link.online_order_id}-${link.event_type}`}
                          size="small"
                          column={2}
                        >
                          <Descriptions.Item label="Commande">
                            {link.order_number}
                          </Descriptions.Item>
                          <Descriptions.Item label="Écriture">
                            <Tag color={link.event_type === "capture" ? "green" : "red"}>
                              {link.event_type === "capture"
                                ? "Encaissement"
                                : "Remboursement"}
                            </Tag>
                          </Descriptions.Item>
                        </Descriptions>
                      ))}
                    </Card>
                  )}
              </div>
            ) : (
              <Spin />
            )}
          </Modal>
        </div>
      </Content>
    </Layout>
  );
};

export default PaymentEntrant;
